import { type ChangeEvent, memo, useCallback, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { css, type Theme } from '@emotion/react';
import { ConfigurationInput } from 'client/components/Input/ConfigurationInput';
import { useComponentUpdate } from 'client/sections/components/componentUtils';
import { useProjectOrDeploymentState } from 'client/sections/sectionsUtils';

interface EnvironmentInputProps {
  stackComponentID: number | null;
  index: number;
  path?: string;
}

export const EnvironmentInput = memo(({ stackComponentID, index, path }: EnvironmentInputProps) => {
  const keyRef = useRef<HTMLInputElement | null>(null);
  const valueRef = useRef<HTMLInputElement | null>(null);

  const env = useProjectOrDeploymentState(
    (state) =>
      state.document?.componentsWithValues.find((component) => component.stackComponentID === stackComponentID)?.environmentVariables[
        index
      ],
    shallow
  );

  /**
   * Enable changing textarea height based on content.
   */
  const handleRef = (element: HTMLInputElement | null) => {
    if (!element) return;
    element.parentElement?.parentElement?.setAttribute('style', `--height: ${element.scrollHeight}px`);
  };

  const handleKeyRef = (element: HTMLInputElement | null) => {
    keyRef.current = element;
    handleRef(element);
  };

  const handleValueRef = (element: HTMLInputElement | null) => {
    valueRef.current = element;
    handleRef(element);
  };

  const handleInput = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    handleRef(event.target);
  }, []);

  const handleKeyChange = useComponentUpdate(
    stackComponentID,
    (state) => (value: string | number | boolean | null | undefined) => {
      if (value === null || value === undefined) return;
      if (!state.environmentVariables[index]) {
        state.environmentVariables[index] = { identifier: `${value}`, value: '' };
      } else {
        state.environmentVariables[index].identifier = `${value}`;
      }
    },
    [index]
  );

  const handleValueChange = useComponentUpdate(
    stackComponentID,
    (state) => (value: string | number | boolean | null | undefined) => {
      if (value === null || value === undefined) return;
      if (!state.environmentVariables[index]) {
        state.environmentVariables[index] = { identifier: '', value: `${value}` };
      } else {
        state.environmentVariables[index].value = `${value}`;
      }
    },
    [index]
  );

  return (
    <div css={inputGroupStyle}>
      <ConfigurationInput
        id={`env.${index}.key`}
        label=""
        value={env?.identifier || ''}
        path={path + '.identifier'}
        rows={1}
        onInput={handleInput}
        onChange={handleKeyChange}
        ref={handleKeyRef}
        multiline
        css={inputStyle}
      />
      <ConfigurationInput
        id={`env.${index}.value`}
        label=""
        value={env?.value || ''}
        path={path + '.value'}
        rows={1}
        onInput={handleInput}
        onChange={handleValueChange}
        ref={handleValueRef}
        multiline
        css={inputStyle}
      />
    </div>
  );
});

const inputGroupStyle = (theme: Theme) => css`
  display: flex;
  align-items: stretch;
  gap: 2px;

  fieldset {
    min-height: min(var(--height), 164px);
  }

  .input {
    display: flex;
    align-items: stretch;
    margin-bottom: 0 !important;
    min-height: min(var(--height), 164px);

    textarea {
      border-radius: 0;
    }
  }

  &:first-of-type {
    > fieldset:first-of-type > .input textarea {
      border-radius: ${theme.borderRadius}px 0 0 0;
    }

    > fieldset:last-of-type > .input textarea {
      border-radius: 0 ${theme.borderRadius}px 0 0;
    }
  }

  &:last-of-type {
    > fieldset:first-of-type > .input textarea {
      border-radius: 0 0 0 ${theme.borderRadius}px;
    }

    > fieldset:last-of-type > .input textarea {
      border-radius: 0 0 ${theme.borderRadius}px 0;
    }
  }

  &:first-of-type:last-of-type {
    > fieldset:first-of-type > .input textarea {
      border-radius: ${theme.borderRadius}px 0 0 ${theme.borderRadius}px;
    }

    > fieldset:last-of-type > .input textarea {
      border-radius: 0 ${theme.borderRadius}px ${theme.borderRadius}px 0;
    }
  }
`;

const inputStyle = css`
  label {
    display: none;
  }

  textarea {
    padding: 8px 12px;
  }

  > .icon {
    padding: 0 8px;

    > .body2 {
      display: none;
    }
  }
`;
